import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiPhone, FiPhoneOff } from 'react-icons/fi';
import { getSocket } from '../../services/socket';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import './VideoCall.css';

const METERED_DOMAIN = process.env.REACT_APP_METERED_DOMAIN;
const FRAME_SDK_URL = 'https://cdn.metered.ca/sdk/frame/1.4.3/sdk-frame.min.js';

// Load Metered's Frame SDK once. Unlike a bare <iframe>, it tells us when the
// user hangs up from inside the call UI (`meetingLeft`), so we can leave the
// call page instead of showing Metered's "Thanks for using" screen.
let sdkPromise = null;
const loadFrameSdk = () => {
  if (window.MeteredFrame) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = FRAME_SDK_URL;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        sdkPromise = null;
        reject(new Error('Could not load the video call SDK'));
      };
      document.body.appendChild(script);
    });
  }
  return sdkPromise;
};

function VideoCall() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const callType = urlParams.get('type') || 'video';
  const isAudio = callType === 'audio';

  const [status, setStatus] = useState('Connecting...');
  // Until someone else is in the call we cover Metered's UI (which otherwise
  // shows its "Invite Instructions" box) with our own calling screen
  const [otherJoined, setOtherJoined] = useState(false);
  const [ended, setEnded] = useState(false);
  // If Metered never reports a join, stop covering its UI so any prompt it
  // shows (mic permission, join button, error) is visible instead of an
  // endless "Connecting..."
  const [revealFrame, setRevealFrame] = useState(false);
  const [inMeeting, setInMeeting] = useState(false);
  const socket = getSocket();
  const cleanedUp = useRef(false);
  const frameRef = useRef(null);
  const containerRef = useRef(null);

  const channel = useChatStore((s) => s.channels.find((c) => c._id === channelId));
  const { user } = useAuthStore();
  const myId = String(user?._id || user?.id || '');
  const isDirectRef = useRef(false);
  isDirectRef.current = channel?.type === 'direct';

  const otherMember = channel?.type === 'direct'
    ? channel.members?.find((m) => String(m._id || m) !== myId)
    : null;
  const peerName = otherMember?.firstName
    ? `${otherMember.firstName} ${otherMember.lastName || ''}`.trim()
    : channel?.name || '';
  const peerAvatar = otherMember?.avatar || otherMember?.profilePicture;

  // Leave to the chat this call belongs to. navigate(-1) broke when the call
  // was opened straight from a notification (no history to go back to).
  const leave = useCallback(
    () => navigate(`/dashboard/channel/${channelId}`, { replace: true }),
    [navigate, channelId]
  );

  // Single exit path: tell the server, leave Metered, go back to the chat
  const finishCall = useCallback(() => {
    if (cleanedUp.current) return;
    cleanedUp.current = true;
    setEnded(true);
    socket?.emit('call:end', { channelId });
    try { frameRef.current?.leave(); } catch { /* frame already gone */ }
    leave();
  }, [socket, channelId, leave]);

  useEffect(() => {
    // Ignore a join that finishes after this effect was torn down (StrictMode
    // double-mount or leaving mid-connect) so it can't revive the call.
    let cancelled = false;
    let revealTimer = null;
    cleanedUp.current = false;
    const container = containerRef.current;

    const join = async () => {
      try {
        const roomRes = await api.post('/call/room', { channelId, callType });
        const { roomName } = roomRes.data;

        const tokenRes = await api.post('/call/token', { roomName, callType });
        const { token, userName } = tokenRes.data;

        await loadFrameSdk();
        if (cancelled || !container) return;

        const frame = new window.MeteredFrame();
        frameRef.current = frame;
        frame.init({
          roomURL: `${METERED_DOMAIN}/${roomName}`,
          autoJoin: true,
          name: userName,
          accessToken: token,
          joinVideoOn: !isAudio,
          joinAudioOn: true,
          disableScreenSharing: isAudio,
          disableChat: true,
          showInviteBox: false,
          width: '100%',
          height: '100%',
        }, container);

        let meetingJoined = false;
        revealTimer = setTimeout(() => {
          if (cancelled || meetingJoined) return;
          setRevealFrame(true);
          setStatus('Waiting for call window…');
        }, 10 * 1000);

        frame.on('meetingJoined', () => {
          if (cancelled) return;
          meetingJoined = true;
          setInMeeting(true);
          setStatus(isDirectRef.current ? 'Ringing...' : 'In call — others join as they accept');
        });

        // Hung up using the button inside the Metered UI
        frame.on('meetingLeft', () => {
          if (!cancelled) finishCall();
        });

        // 1:1 call: once the other person has been in and left, end ours too
        let someoneJoined = false;
        const otherLeft = () => {
          if (cancelled || !someoneJoined || !isDirectRef.current) return;
          setStatus('Call ended');
          finishCall();
        };
        frame.on('onlineParticipants', (data) => {
          const participants = Array.isArray(data) ? data : data?.participants;
          if (cancelled || !Array.isArray(participants)) return;
          meetingJoined = true;
          if (participants.length > 1) {
            someoneJoined = true;
            setOtherJoined(true);
            setStatus('Connected');
          } else {
            otherLeft();
          }
        });
        frame.on('participantJoined', () => {
          if (cancelled) return;
          meetingJoined = true;
          someoneJoined = true;
          setOtherJoined(true);
          setStatus('Connected');
        });
        frame.on('participantLeft', otherLeft);

        socket?.emit('call:join', { channelId, callType });
      } catch (err) {
        if (cancelled) return;
        console.error('[VideoCall] Error:', err);
        alert(err.response?.data?.message || err.message || 'Could not join the call. Please try again.');
        cleanedUp.current = true;
        leave();
      }
    };

    join();

    // 1:1 call declined by the other person: hang up instead of waiting alone
    const onRejected = ({ channelId: rejectedId, userName, isDirect }) => {
      if (rejectedId !== channelId || !isDirect) return;
      setStatus(`${userName || 'They'} declined the call`);
      setTimeout(() => { if (!cancelled) finishCall(); }, 2000);
    };
    socket?.on('call:rejected', onRejected);

    // 1:1 call: the other person pressed Leave/cut (server relay, works even
    // if Metered's own participant events are late)
    const onEnded = ({ userId: endedBy }) => {
      if (cancelled || !isDirectRef.current || String(endedBy) === myId) return;
      setStatus('Call ended');
      finishCall();
    };
    socket?.on('call:ended', onEnded);

    return () => {
      cancelled = true;
      clearTimeout(revealTimer);
      socket?.off('call:rejected', onRejected);
      socket?.off('call:ended', onEnded);
      // Leaving Metered also stops camera/mic/screen share
      try { frameRef.current?.leave(); } catch { /* frame already gone */ }
      frameRef.current = null;
      if (container) container.innerHTML = '';
      if (!cleanedUp.current) {
        cleanedUp.current = true;
        socket?.emit('call:end', { channelId });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="video-call">
      <div className="video-call-header">
        <h2>{isAudio ? 'Voice Call' : 'Video Call'}</h2>
        <span className="video-call-status" style={{ color: status === 'Connected' ? '#2eb67d' : '#f0a500' }}>
          {status}
        </span>
        <button className="close-call-btn" onClick={finishCall}>
          <FiX /> Leave Call
        </button>
      </div>

      <div className="metered-iframe-wrapper">
        <div ref={containerRef} className="metered-frame-container" />
        {/* 1:1: ringing screen until the other person picks up.
            Group: only until we're in — people join one by one as they accept. */}
        {((isDirectRef.current ? !otherJoined : !inMeeting) && !revealFrame) || ended ? (
          <div className="call-overlay">
            <div className={`call-overlay-avatar ${ended ? '' : 'ringing'}`}>
              {peerAvatar ? <img src={peerAvatar} alt="" /> : (peerName ? peerName[0].toUpperCase() : <FiPhone size={36} />)}
            </div>
            {peerName && <div className="call-overlay-name">{peerName}</div>}
            <div className="call-overlay-status">{ended ? 'Call ended' : status}</div>
            {!ended && (
              <button className="call-overlay-hangup" onClick={finishCall} aria-label="Cancel call">
                <FiPhoneOff size={22} />
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default VideoCall;
